/**
 * Anwesenheiten v2 — Lokale Stempelzeiten + tages_arbeitszeit.
 *
 * Tabs: Tagesansicht, Monatsansicht, Statistiken, Einstellungen.
 * Backend: /api/anwesenheiten/* (anwesenheiten.js), Korrekturen via /api/stempel/* (stempeluhr.js).
 *
 * @page /anwesenheiten
 * @roles admin, manager
 * @api /api/anwesenheiten/tag/:datum, /api/anwesenheiten/monat/:monat
 * @api /api/anwesenheiten/statistiken, /api/anwesenheiten/korrekturen
 * @api /api/anwesenheiten/export/csv, /api/anwesenheiten/export/pdf
 * @api /api/anwesenheiten/abweichungen/tag, /api/anwesenheiten/abweichungen/monat
 * @api /api/stempel/korrektur, DELETE /api/stempel/:id, /api/stempel/regeln
 * @i18n attendance
 * @since v2.229.0, overhaul v2.233.0
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MitarbeiterAvatar from '../components/MitarbeiterAvatar';
import MitarbeiterListItem from '../components/MitarbeiterListItem';
import { Download, RefreshCw, Copy, ExternalLink, Save } from 'lucide-react';
import { PageHeader } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import ModulEinstellungen from '../components/ModulEinstellungen';

/* ─── Helpers ─── */
function pad2(n) { return String(n).padStart(2, '0'); }
function fmtDate(d) { return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; }
function fmtMinutes(min) {
  if (!min || min === 0) return '–';
  if (min < 60) return `${Math.round(min)}min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}
function fmtMinAsHours(min) {
  if (!min) return '–';
  const h = Math.floor(Math.abs(min) / 60);
  const m = Math.abs(min) % 60;
  const sign = min < 0 ? '-' : (min > 0 ? '+' : '');
  return `${sign}${h}:${pad2(m)}`;
}
function fmtHours(h) { if (h == null || h === 0) return '–'; return (Math.round(h * 10) / 10).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'h'; }

const API = import.meta.env.VITE_API_URL || '';

/* ─── Status Config ─── */
const LIVE_STATUS = {
  working:     { label: 'attendance.live.working',     dot: 'bg-emerald-500', pulse: true  },
  short_break: { label: 'attendance.live.short_break', dot: 'bg-amber-500',   pulse: false },
  lunch_break: { label: 'attendance.live.lunch_break', dot: 'bg-amber-500',   pulse: false },
  finished:    { label: 'attendance.live.finished',    dot: 'bg-gray-400',    pulse: false },
  absent:      { label: 'attendance.live.absent',      dot: 'bg-red-400',     pulse: false },
  unknown:     { label: 'attendance.live.unknown',     dot: 'bg-gray-400',    pulse: false },
};

/* ─── Department Label Mapping ─── */
const DEPT_LABELS = {
  outbound: 'attendance.dept.outbound',
  inbound_retouren: 'attendance.dept.inbound_retouren',
  verwaltung: 'attendance.dept.verwaltung',
};

/* ─── Chevron Icon ─── */
function ChevronIcon({ open }) {
  return (
    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
      className={`transition-transform ${open ? 'rotate-90' : ''}`}>
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

/* ─── Shared KPI Card ─── */
function KpiCard({ label, value, sub }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-4 flex-1 min-w-[130px]">
      <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider mb-1">{label}</div>
      <div className="text-xl font-mono font-extrabold text-[var(--text-primary)]">{value}</div>
      {sub && <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{sub}</div>}
    </div>
  );
}

/* ─── Spinner ─── */
function Spinner({ text }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-12 text-center">
      <div className="inline-block w-6 h-6 border-2 border-[#338edc]/30 border-t-[#338edc] rounded-full animate-spin mb-3" />
      {text && <div className="text-sm text-[var(--text-secondary)]">{text}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  CorrectionModal                                                */
/* ═══════════════════════════════════════════════════════════════ */
/** Formatiert Datum/ISO-String in lokales "YYYY-MM-DD" + "HH:mm". */
function splitLocalDateTime(input) {
  if (!input) return { date: '', time: '' };
  const d = new Date(input);
  if (isNaN(d.getTime())) return { date: '', time: '' };
  const pad = n => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function CorrectionModal({ stempel, onClose, onSaved }) {
  const { t } = useLanguage();
  const isEdit = !!stempel?.id;
  const initial = splitLocalDateTime(stempel?.zeitpunkt);
  const [typ, setTyp] = useState(stempel?.typ || 'arbeit_start');
  const [dateVal, setDateVal] = useState(initial.date);
  const [timeVal, setTimeVal] = useState(initial.time);
  const [notiz, setNotiz] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const typOptions = ['arbeit_start', 'kurze_pause', 'pause_ende', 'mittagspause', 'mittag_ende', 'arbeitsende'];

  // Local "YYYY-MM-DDTHH:mm" → ISO UTC string (für TIMESTAMPTZ im Backend).
  function buildIsoZeit() {
    if (!dateVal || !timeVal) return null;
    const d = new Date(`${dateVal}T${timeVal}`);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    const isoZeit = buildIsoZeit();
    if (!isoZeit) {
      setError(t('attendance.invalid_datetime', 'Ungültiges Datum/Zeit'));
      setSaving(false);
      return;
    }
    try {
      const origIso = stempel?.zeitpunkt ? new Date(stempel.zeitpunkt).toISOString() : null;
      const res = await fetch(`${API}/api/stempel/korrektur`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit ? {
          stempel_id: stempel.id,
          neuer_typ: typ !== stempel.typ ? typ : undefined,
          neue_zeit: isoZeit !== origIso ? isoZeit : undefined,
          notiz: notiz || undefined,
        } : {
          mitarbeiter_id: stempel?.mitarbeiter_id,
          typ,
          zeitpunkt: isoZeit,
          notiz: notiz || undefined,
        }),
      });
      if (res.ok) onSaved();
      else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || t('attendance.error_generic'));
      }
    } catch {
      setError(t('attendance.error_generic'));
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!stempel?.id) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/stempel/${stempel.id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) onSaved();
      else setError(t('attendance.error_generic'));
    } catch {
      setError(t('attendance.error_generic'));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-6 w-full max-w-md shadow-sm" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-[var(--text-primary)] mb-4">
          {isEdit ? t('attendance.edit_stamp') : t('attendance.add_stamp')}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('attendance.stamp_type')}</label>
            <select value={typ} onChange={e => setTyp(e.target.value)}
              className="w-full bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]">
              {typOptions.map(o => <option key={o} value={o}>{t(`attendance.typ.${o}`, o)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('attendance.stamp_date', 'Datum')}</label>
              <input type="date" value={dateVal} onChange={e => setDateVal(e.target.value)}
                className="w-full bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]" />
            </div>
            <div>
              <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('attendance.stamp_time_only', 'Uhrzeit')}</label>
              <input type="time" value={timeVal} onChange={e => setTimeVal(e.target.value)}
                className="w-full bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('attendance.note')}</label>
            <textarea value={notiz} onChange={e => setNotiz(e.target.value)} rows={2}
              className="w-full bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] resize-none" />
          </div>
          {error && (
            <div className="text-xs text-red-500 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>
          )}
        </div>
        <div className="flex items-center justify-between mt-5">
          {isEdit && !confirmDelete && (
            <button onClick={() => setConfirmDelete(true)} disabled={saving}
              className="text-xs text-red-500 hover:text-red-400 disabled:opacity-40">{t('attendance.delete_stamp')}</button>
          )}
          {isEdit && confirmDelete && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-500">{t('attendance.confirm_delete_stamp')}</span>
              <button onClick={() => setConfirmDelete(false)} disabled={saving}
                className="px-2 py-1 text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">{t('common.cancel')}</button>
              <button onClick={handleDelete} disabled={saving}
                className="px-2.5 py-1 text-[11px] font-semibold bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-40">
                {saving ? '...' : t('common.delete', 'Löschen')}
              </button>
            </div>
          )}
          {!confirmDelete && (
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={onClose} className="px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]">{t('common.cancel')}</button>
              <button onClick={handleSave} disabled={saving || !typ || !dateVal || !timeVal}
                className="px-4 py-1.5 text-xs font-semibold bg-[#338edc] text-white rounded-lg hover:bg-[#2a7bc8] disabled:opacity-40">
                {saving ? '...' : t('common.save')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  DayView                                                        */
/* ═══════════════════════════════════════════════════════════════ */
function DayView({ onEmployeeClick, maFilter }) {
  const { t, locale } = useLanguage();
  const [date, setDate] = useState(fmtDate(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [onlyPresent, setOnlyPresent] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [editStempel, setEditStempel] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const refreshRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/stempel/can-edit`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { canEdit: false })
      .then(d => setCanEdit(!!d.canEdit))
      .catch(() => setCanEdit(false));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/anwesenheiten/tag/${date}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!d.error) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (date !== fmtDate(new Date())) return;
    refreshRef.current = setInterval(load, 60000);
    return () => clearInterval(refreshRef.current);
  }, [date, load]);

  const prevDay = () => { const d = new Date(date); d.setDate(d.getDate() - 1); setDate(fmtDate(d)); };
  const nextDay = () => { const d = new Date(date); d.setDate(d.getDate() + 1); setDate(fmtDate(d)); };
  const goToday = () => setDate(fmtDate(new Date()));

  const isToday = date === fmtDate(new Date());

  const filtered = useMemo(() => {
    if (!data?.mitarbeiter) return [];
    let rows = data.mitarbeiter;
    if (maFilter) rows = rows.filter(r => String(r.mitarbeiter_id) === maFilter);
    if (search) { const q = search.toLowerCase(); rows = rows.filter(r => r.name.toLowerCase().includes(q)); }
    if (filterDept) rows = rows.filter(r => r.kostenstelle === filterDept);
    if (onlyPresent) {
      if (isToday) {
        rows = rows.filter(r => r.live_status === 'working' || r.live_status === 'short_break' || r.live_status === 'lunch_break');
      } else {
        rows = rows.filter(r => Array.isArray(r.stempel) && r.stempel.length > 0);
      }
    }
    return rows;
  }, [data, search, filterDept, maFilter, onlyPresent, isToday]);

  const departments = useMemo(() => {
    if (!data?.mitarbeiter) return [];
    return [...new Set(data.mitarbeiter.map(r => r.kostenstelle).filter(Boolean))].sort();
  }, [data]);

  return (
    <div>
      {/* Date picker + filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-1">
          <button onClick={prevDay} className="p-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset)] text-[var(--text-primary)]">
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7"/></svg>
          </button>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50" />
          <button onClick={nextDay} className="p-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset)] text-[var(--text-primary)]">
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg>
          </button>
          {!isToday && (
            <button onClick={goToday} className="px-2.5 py-1.5 text-xs font-semibold text-[#338edc] border border-[#338edc]/30 rounded-lg hover:bg-[#338edc]/10 ml-1">
              {t('attendance.today')}
            </button>
          )}
        </div>
        <input type="text" placeholder={t('attendance.search_placeholder')} value={search} onChange={e => setSearch(e.target.value)}
          className="bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[#338edc]/50 w-52" />
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)}
          className="bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none">
          <option value="">{t('attendance.all_departments')}</option>
          {departments.map(d => <option key={d} value={d}>{DEPT_LABELS[d] ? t(DEPT_LABELS[d]) : d}</option>)}
        </select>
        <button
          onClick={() => setOnlyPresent(v => !v)}
          className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
            onlyPresent
              ? 'bg-[#338edc] text-white border-[#338edc]'
              : 'bg-[var(--bg-inset)] border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
          }`}
          title={t('attendance.only_present_tooltip', 'Nur anwesende Mitarbeiter anzeigen')}>
          {t('attendance.only_present', 'Nur Anwesende')}
        </button>
        {isToday && (
          <button onClick={load} className="p-2 rounded-lg border border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]" title="Refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <a href={`${API}/api/anwesenheiten/export/csv?monat=${date.slice(0, 7)}`}
            className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] flex items-center gap-1">
            <Download className="w-3 h-3" /> CSV
          </a>
        </div>
        <span className="text-[10px] text-[var(--text-tertiary)]">
          {data?.ist_feiertag ? `${data.ist_feiertag.name}` : ''}
          {isToday ? ` · ${t('attendance.live')}` : ''}
        </span>
      </div>

      {loading && <Spinner text={t('attendance.loading')} />}

      {!loading && data && (
        <>
          {/* KPI Bar */}
          <div className="flex flex-wrap gap-3 mb-5">
            <KpiCard label={t('attendance.kpi.total')} value={data.kpi.gesamt} />
            <KpiCard label={t('attendance.kpi.present')} value={data.kpi.anwesend} />
            {isToday && <KpiCard label={t('attendance.kpi.active')} value={data.kpi.aktiv} />}
            {isToday && <KpiCard label={t('attendance.kpi.on_break')} value={data.kpi.in_pause} />}
            <KpiCard label={t('attendance.kpi.absent')} value={data.kpi.fehlt} />
            <KpiCard label={t('attendance.kpi.avg_hours')} value={`${data.kpi.avg_netto_h}h`} />
          </div>

          {/* Table */}
          <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)] text-[10px] uppercase tracking-wider">
                  <th className="w-8 px-2 py-2.5"></th>
                  <th className="px-3 py-2.5 text-left">{t('attendance.col.employee')}</th>
                  <th className="px-3 py-2.5 text-left">{t('attendance.col.department')}</th>
                  <th className="px-2 py-2.5 text-center">{t('attendance.col.status')}</th>
                  <th className="px-3 py-2.5 text-center">{t('attendance.col.start')}</th>
                  <th className="px-3 py-2.5 text-center">{t('attendance.col.end')}</th>
                  <th className="px-3 py-2.5 text-right">{t('attendance.col.breaks')}</th>
                  <th className="px-3 py-2.5 text-right">{t('attendance.col.netto')}</th>
                  <th className="px-3 py-2.5 text-right">{t('attendance.col.target')}</th>
                  <th className="px-3 py-2.5 text-right">{t('attendance.col.delta')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(ma => {
                  const isExp = expanded === ma.mitarbeiter_id;
                  const st = LIVE_STATUS[ma.live_status] || LIVE_STATUS.unknown;
                  const delta = ma.delta_minuten;
                  return (
                    <React.Fragment key={ma.mitarbeiter_id}>
                      <tr className="border-b border-[var(--border-card,#E5E7EB)]/50 hover:bg-[var(--bg-inset)]/50 cursor-pointer transition-colors"
                        onClick={() => setExpanded(isExp ? null : ma.mitarbeiter_id)}>
                        <td className="px-2 py-2">{(ma.stempel.length > 0 || canEdit) && <ChevronIcon open={isExp} />}</td>
                        <td className="px-3 py-2">
                          <div onClick={e => { e.stopPropagation(); onEmployeeClick(ma.mitarbeiter_id); }} className="hover:text-[#338edc] transition-colors">
                            <MitarbeiterListItem
                              mitarbeiter={{ ...ma, id: ma.mitarbeiter_id }}
                              size="sm"
                              apiBase={API}
                              onClick={() => {}}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2 text-[var(--text-secondary)] text-xs truncate">{DEPT_LABELS[ma.kostenstelle] ? t(DEPT_LABELS[ma.kostenstelle]) : (ma.kostenstelle || '–')}</td>
                        <td className="px-2 py-2 text-center">
                          <span className="inline-flex items-center gap-1 text-[10px]" title={t(st.label)}>
                            <span className={`w-2 h-2 rounded-full ${st.dot} ${st.pulse ? 'animate-pulse' : ''}`} />
                            <span className="hidden sm:inline text-[var(--text-tertiary)]">{t(st.label)}</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center font-mono text-xs">
                          {ma.erster_stempel_zeit ? (
                            <div className="flex flex-col items-center leading-tight">
                              <span>{ma.erster_stempel_zeit}</span>
                              {ma.gezaehlt_ab && (
                                <span
                                  className="text-[9px] text-amber-500 font-sans mt-0.5"
                                  title={t('attendance.gezaehlt_ab_tooltip', 'Zählt erst ab Arbeitsbeginn laut Regel')}>
                                  {t('attendance.gezaehlt_ab_label', 'zählt ab')} {ma.gezaehlt_ab}
                                </span>
                              )}
                            </div>
                          ) : '–'}
                        </td>
                        <td className="px-3 py-2 text-center font-mono text-xs">{ma.letzter_stempel_zeit || '–'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[var(--text-secondary)]">{fmtMinutes(ma.pausen_minuten)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{ma.netto_minuten > 0 ? fmtMinAsHours(ma.netto_minuten).replace(/^[+]/, '') : '–'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[var(--text-secondary)]">{ma.soll_minuten > 0 ? fmtMinAsHours(ma.soll_minuten).replace(/^[+]/, '') : '–'}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${
                          delta > 0 ? 'text-emerald-500' : delta < -15 ? 'text-red-400' : 'text-[var(--text-secondary)]'
                        }`}>
                          {ma.stempel.length > 0 ? fmtMinAsHours(delta) : '–'}
                        </td>
                      </tr>
                      {isExp && (ma.stempel.length > 0 || canEdit) && (
                        <tr className="border-b border-[var(--border-card,#E5E7EB)]">
                          <td colSpan={10} className="px-6 py-3 bg-[var(--bg-inset)]/50">
                            <div className="flex flex-wrap items-center gap-2">
                              {ma.stempel.map(s => (
                                <button key={s.id}
                                  onClick={e => { e.stopPropagation(); setEditStempel({ ...s, mitarbeiter_id: ma.mitarbeiter_id }); }}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition-colors ${
                                    s.korrigiert ? 'border-amber-500/40 bg-amber-500/5' : 'border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card)] hover:border-[#338edc]/40'
                                  }`}
                                  title={s.korrigiert ? `${t('attendance.corrected')} — ${s.original_zeit || ''}` : t('attendance.click_to_edit')}>
                                  <span className="font-mono font-semibold">{s.zeit}</span>
                                  <span className="text-[var(--text-tertiary)]">{t(`attendance.typ.${s.typ}`, s.typ)}</span>
                                  {s.kiosk_geraet_name ? (
                                    <span className="text-[9px] px-1 py-0.5 rounded bg-[var(--bg-inset)] text-[var(--text-tertiary)]">{s.kiosk_geraet_name}</span>
                                  ) : s.quelle && s.quelle !== 'barcode' ? (
                                    <span className="text-[9px] px-1 py-0.5 rounded bg-[var(--bg-inset)] text-[var(--text-tertiary)]">{s.quelle}</span>
                                  ) : null}
                                  {s.offline_nachgetragen && (
                                    <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-500"
                                      title={t('attendance.offline_hint', 'Offline am Terminal erfasst und später nachgeholt — die Uhrzeit stammt vom Tablet.')}>
                                      {t('attendance.offline_kurz', 'offline')}
                                    </span>
                                  )}
                                  {s.gps_ausserhalb && <span className="text-[9px] text-amber-400">GPS!</span>}
                                  {s.korrigiert && <span className="text-[9px] text-amber-400">*</span>}
                                </button>
                              ))}
                              {canEdit && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    setEditStempel({ mitarbeiter_id: ma.mitarbeiter_id, zeitpunkt: `${date}T08:00` });
                                  }}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border border-dashed border-[#338edc]/50 text-[#338edc] hover:bg-[#338edc]/5 transition-colors"
                                  title={t('attendance.add_stamp')}>
                                  <span className="text-sm leading-none">+</span>
                                  <span>{t('attendance.add_stamp')}</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} className="p-8 text-center text-[var(--text-tertiary)] text-sm">{t('attendance.no_employees')}</td></tr>
                )}
              </tbody>
            </table>
            <div className="px-4 py-2 border-t border-[var(--border-card,#E5E7EB)] text-[10px] text-[var(--text-tertiary)] flex justify-between">
              <span>{filtered.length} {t('attendance.of')} {data.kpi.gesamt} {t('attendance.employees_label')}</span>
            </div>
          </div>
        </>
      )}

      {editStempel && (
        <CorrectionModal stempel={editStempel} onClose={() => setEditStempel(null)} onSaved={() => { setEditStempel(null); load(); }} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  MonthView — Calendar Grid + SOLL column + Footer totals       */
/* ═══════════════════════════════════════════════════════════════ */
function MonthView({ onEmployeeClick, maFilter }) {
  const { t, tMonth } = useLanguage();
  const now = new Date();
  const [monat, setMonat] = useState(now.getMonth());
  const [jahr, setJahr] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedMa, setSelectedMa] = useState(null);

  const monatStr = `${jahr}-${pad2(monat + 1)}`;

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/anwesenheiten/monat/${monatStr}`, { credentials: 'include' })
      .then(r => r.json()).then(d => { if (!d.error) setData(d); }).catch(() => {})
      .finally(() => setLoading(false));
  }, [monatStr]);

  const prevMonth = () => { if (monat === 0) { setMonat(11); setJahr(y => y - 1); } else setMonat(m => m - 1); };
  const nextMonth = () => {
    const isCurrent = jahr === now.getFullYear() && monat === now.getMonth();
    if (isCurrent) return;
    if (monat === 11) { setMonat(0); setJahr(y => y + 1); } else setMonat(m => m + 1);
  };

  const filtered = useMemo(() => {
    if (!data?.mitarbeiter) return [];
    let rows = data.mitarbeiter;
    if (maFilter) rows = rows.filter(r => String(r.mitarbeiter_id) === maFilter);
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => r.name.toLowerCase().includes(q));
  }, [data, search, maFilter]);

  const daysInMonth = data?.tage_im_monat || new Date(jahr, monat + 1, 0).getDate();
  const dayHeaders = [];
  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${jahr}-${pad2(monat + 1)}-${pad2(d)}`;
    const dow = new Date(jahr, monat, d).getDay();
    dayHeaders.push({ d, dateStr, dow, dayName: dayNames[dow], isWE: dow === 0 || dow === 6, isFT: !!data?.feiertage?.[dateStr] });
  }

  // Footer team totals
  const teamTotals = useMemo(() => {
    if (!filtered.length) return { netto: 0, soll: 0, delta: 0 };
    return {
      netto: filtered.reduce((s, ma) => s + (ma.summe_netto || 0), 0),
      soll: filtered.reduce((s, ma) => s + (ma.summe_soll || 0), 0),
      delta: filtered.reduce((s, ma) => s + (ma.summe_delta || 0), 0),
    };
  }, [filtered]);

  function cellColor(netto, soll) {
    if (soll <= 0) return '';
    if (netto <= 0) return 'text-red-400';
    const pct = netto / soll;
    if (pct >= 0.95) return 'text-emerald-500';
    if (pct >= 0.5) return 'text-amber-400';
    return 'text-red-400';
  }

  const ABW_COLORS = {
    urlaub: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-500', short: 'U' },
    zeitausgleich: { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-500', short: 'ZA' },
    krankenstand: { bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-500', short: 'K' },
    pflegefreistellung: { bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-400', short: 'PF' },
    sonderurlaub: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-500', short: 'SU' },
    bildungskarenz: { bg: 'bg-teal-50 dark:bg-teal-900/20', text: 'text-teal-500', short: 'BK' },
    mutterschutz: { bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-500', short: 'MU' },
    elternkarenz: { bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-400', short: 'EK' },
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-1">
          <button onClick={prevMonth} className="p-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset)] text-[var(--text-primary)]">
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7"/></svg>
          </button>
          <span className="text-sm text-[var(--text-primary)] font-medium min-w-[130px] text-center">{tMonth(monat)} {jahr}</span>
          <button onClick={nextMonth}
            disabled={jahr === now.getFullYear() && monat === now.getMonth()}
            className={`p-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] transition-colors ${
              jahr === now.getFullYear() && monat === now.getMonth() ? 'text-[var(--text-muted)]' : 'hover:bg-[var(--bg-inset)] text-[var(--text-primary)]'
            }`}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg>
          </button>
        </div>
        <input type="text" placeholder={t('attendance.search_placeholder')} value={search} onChange={e => setSearch(e.target.value)}
          className="bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none w-48" />
        <div className="flex items-center gap-2 ml-auto">
          <a href={`${API}/api/anwesenheiten/export/csv?monat=${monatStr}`} className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] flex items-center gap-1">
            <Download className="w-3 h-3" /> CSV
          </a>
          {selectedMa && (
            <a href={`${API}/api/anwesenheiten/export/pdf?monat=${monatStr}&mitarbeiter_id=${selectedMa}`}
              className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] flex items-center gap-1">
              <Download className="w-3 h-3" /> PDF
            </a>
          )}
        </div>
      </div>

      {loading && <Spinner text={t('attendance.loading')} />}

      {!loading && data && (
        <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-x-auto">
          <table className="text-[10px] min-w-max">
            <thead>
              <tr className="border-b border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] sticky top-0 z-10">
                <th className="px-2 py-2 text-left text-[var(--text-tertiary)] sticky left-0 bg-[var(--bg-inset,#F8F9FC)] min-w-[160px]">
                  {t('attendance.col.employee')}
                </th>
                {dayHeaders.map(h => (
                  <th key={h.d} className={`px-0.5 py-2 text-center min-w-[32px] ${
                    h.isFT ? 'text-rose-400 bg-rose-50/60 dark:bg-rose-900/15' :
                    h.isWE ? 'text-[var(--text-muted)] bg-[var(--bg-inset)]' :
                    'text-[var(--text-secondary)]'
                  }`}>
                    <div>{h.dayName}</div>
                    <div className="font-mono">{h.d}</div>
                    {h.isFT && <div className="text-[7px] text-rose-400 leading-tight" title={data?.feiertage?.[h.dateStr]}>F</div>}
                  </th>
                ))}
                <th className="px-2 py-2 text-right text-[var(--text-secondary)] min-w-[50px]">{t('attendance.col.sum')}</th>
                <th className="px-2 py-2 text-right text-[var(--text-secondary)] min-w-[50px]">{t('attendance.col.target')}</th>
                <th className="px-2 py-2 text-right text-[var(--text-secondary)] min-w-[50px]">{t('attendance.col.delta')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(ma => (
                <tr key={ma.mitarbeiter_id}
                  className={`border-b border-[var(--border-card,#E5E7EB)]/30 hover:bg-[var(--bg-inset)]/30 transition-colors ${selectedMa === ma.mitarbeiter_id ? 'bg-[#338edc]/5' : ''}`}
                  onClick={() => setSelectedMa(selectedMa === ma.mitarbeiter_id ? null : ma.mitarbeiter_id)}>
                  <td className="px-2 py-1.5 sticky left-0 bg-[var(--bg-card)]">
                    <button onClick={e => { e.stopPropagation(); onEmployeeClick(ma.mitarbeiter_id); }}
                      className="font-medium text-[var(--text-primary)] hover:text-[#338edc] truncate max-w-[150px] block text-left text-[11px]">
                      {ma.name}
                    </button>
                  </td>
                  {dayHeaders.map(h => {
                    const day = ma.tage?.[h.dateStr];
                    const netto = day?.netto_minuten || 0;
                    const soll = day?.soll_minuten || 0;
                    const abw = day?.abwesenheit;
                    const isFuture = day?.ist_zukunft;
                    const tagTyp = day?.tag_typ || null;
                    const abwStyle = abw && ABW_COLORS[abw];
                    // #848: Arbeit außerhalb Arbeitszeitmodell — Priorität VOR Sa/So/FT-Badges
                    const isWochenendArbeit = tagTyp === 'wochenende_arbeit';
                    const isFeiertagGearbeitet = tagTyp === 'feiertag_gearbeitet';
                    // v2.365.1 (Teil B): Priorität Sa/So > Feiertag > Abw > Stempel > leer.
                    // Badges statt Stunden für FT/U/K/ZA (Anwesenheits-Sicht, nicht Abrechnung).
                    const ftName = h.isFT ? (data?.feiertage?.[h.dateStr] || '') : '';
                    let tooltip = h.isWE ? t('attendance.tooltip.weekend', 'Wochenende')
                      : h.isFT ? `${ftName || t('attendance.legend.holiday', 'Feiertag')} — ${t('attendance.tooltip.paid_day', 'Bezahlter Tag, SOLL wird als erfüllt gewertet, Delta 0.')}`
                      : abw ? `${t(`attendance.abw.${abw}`, abw)} — ${t('attendance.tooltip.paid_day', 'Bezahlter Tag, SOLL wird als erfüllt gewertet, Delta 0.')}`
                      : '';
                    let cellContent = '–';
                    let cellClass = '';
                    let cellBg = '';
                    if (isFuture) {
                      cellContent = '';
                      cellBg = 'bg-[var(--bg-inset)]/30';
                    } else if (isFeiertagGearbeitet) {
                      // #848: Feiertag mit Arbeit — zeige Mehr-Arbeit (delta über Modell-SOLL)
                      const plus = Math.max(0, netto - soll);
                      cellContent = plus > 0 ? `+${(plus / 60).toFixed(1)}` : 'F';
                      cellBg = 'bg-amber-50 dark:bg-amber-900/20';
                      cellClass = 'text-amber-600 dark:text-amber-400 font-semibold';
                      tooltip = `${ftName || t('attendance.legend.holiday', 'Feiertag')} — ${t('attendance.tooltip.feiertag_gearbeitet', 'Gearbeitet am Feiertag. SOLL wie Modell, nur Plusstunden in IST.')}`;
                    } else if (isWochenendArbeit) {
                      // #848: Sa/So-Arbeit — Ist-Stunden voll in IST
                      cellContent = netto > 0 ? (netto / 60).toFixed(1) : '–';
                      cellBg = 'bg-cyan-50 dark:bg-cyan-900/20';
                      cellClass = 'text-cyan-600 dark:text-cyan-400 font-semibold';
                      tooltip = t('attendance.tooltip.wochenende_arbeit', 'Arbeit außerhalb Arbeitszeitmodell (Sa/So). Stunden voll als Plus in IST.');
                    } else if (h.isWE) {
                      cellContent = '';
                      cellBg = 'bg-[var(--bg-inset)]';
                    } else if (h.isFT) {
                      cellContent = 'F';
                      cellBg = 'bg-rose-50 dark:bg-rose-900/20';
                      cellClass = 'text-rose-500 font-semibold';
                    } else if (abwStyle) {
                      cellContent = abwStyle.short;
                      cellBg = abwStyle.bg;
                      cellClass = `${abwStyle.text} font-semibold`;
                    } else if (netto > 0) {
                      cellContent = (netto / 60).toFixed(1);
                      cellClass = cellColor(netto, soll);
                    }
                    return (
                      <td key={h.d} className={`px-0.5 py-1.5 text-center font-mono ${cellBg} ${cellClass}`} title={tooltip}>
                        {cellContent}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right font-mono font-semibold text-[var(--text-primary)]">
                    {ma.summe_netto > 0 ? (ma.summe_netto / 60).toFixed(1) : '–'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-[var(--text-secondary)]">
                    {ma.summe_soll > 0 ? (ma.summe_soll / 60).toFixed(1) : '–'}
                  </td>
                  <td className={`px-2 py-1.5 text-right font-mono font-bold ${
                    ma.summe_delta > 0 ? 'text-emerald-500' : ma.summe_delta < -60 ? 'text-red-400' : 'text-[var(--text-secondary)]'
                  }`}>
                    {ma.summe_soll > 0 ? fmtMinAsHours(ma.summe_delta) : '–'}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={daysInMonth + 4} className="p-6 text-center text-[var(--text-tertiary)]">{t('attendance.no_employees')}</td></tr>
              )}
            </tbody>
            {/* Footer totals */}
            {filtered.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] font-bold">
                  <td className="px-2 py-2 sticky left-0 bg-[var(--bg-inset,#F8F9FC)] text-[11px] text-[var(--text-secondary)] uppercase">
                    {t('attendance.footer_team_total')}
                  </td>
                  {dayHeaders.map(h => <td key={h.d}></td>)}
                  <td className="px-2 py-2 text-right font-mono text-[var(--text-primary)]">
                    {(teamTotals.netto / 60).toFixed(1)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-[var(--text-secondary)]">
                    {(teamTotals.soll / 60).toFixed(1)}
                  </td>
                  <td className={`px-2 py-2 text-right font-mono ${teamTotals.delta > 0 ? 'text-emerald-500' : teamTotals.delta < 0 ? 'text-red-400' : 'text-[var(--text-secondary)]'}`}>
                    {fmtMinAsHours(teamTotals.delta)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
          {/* Legend */}
          <div className="px-3 py-2.5 border-t border-[var(--border-card,#E5E7EB)]">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[9px]">
              <span className="text-[var(--text-tertiary)]">{filtered.length} {t('attendance.employees_label')}{data.backfilled > 0 ? ` · ${data.backfilled} ${t('attendance.days_computed')}` : ''}</span>
              <span className="text-[var(--text-muted)]">|</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)]" />{t('attendance.legend.weekend')}</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800" /><span className="text-rose-500 font-semibold">F</span> {t('attendance.legend.holiday')}</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" /><span className="text-blue-500 font-semibold">U</span> {t('attendance.legend.vacation')}</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800" /><span className="text-rose-500 font-semibold">K</span> {t('attendance.legend.sick')}</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800" /><span className="text-indigo-500 font-semibold">ZA</span> {t('attendance.legend.comp_time')}</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200" /><span className="text-cyan-600 font-semibold">Sa/So+</span> {t('attendance.legend.wochenende_arbeit', 'Sa/So-Arbeit')}</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200" /><span className="text-amber-600 font-semibold">F+</span> {t('attendance.legend.feiertag_arbeit', 'Feiertag-Arbeit')}</span>
              <span className="text-[var(--text-muted)]">|</span>
              <span className="text-emerald-500 font-semibold">{t('attendance.legend.gte_95')}</span>
              <span className="text-amber-400 font-semibold">{t('attendance.legend.gte_50')}</span>
              <span className="text-red-400 font-semibold">{t('attendance.legend.lt_50')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  StatsView — Extended with Pausen + Pünktlichkeit + Abw.       */
/* ═══════════════════════════════════════════════════════════════ */
function StatsView({ onEmployeeClick }) {
  const { t, tMonth, locale } = useLanguage();
  const now = new Date();
  const [monat, setMonat] = useState(now.getMonth());
  const [jahr, setJahr] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('overview');

  // Abweichungen state
  const [devDate, setDevDate] = useState(fmtDate(now));
  const [devMonat, setDevMonat] = useState(`${now.getFullYear()}-${pad2(now.getMonth() + 1)}`);
  const [dayDevData, setDayDevData] = useState(null);
  const [monthDevData, setMonthDevData] = useState(null);
  const [devLoading, setDevLoading] = useState(false);
  const [devExpanded, setDevExpanded] = useState(null);
  const [devView, setDevView] = useState('daily');

  const monatStr = `${jahr}-${pad2(monat + 1)}`;

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/anwesenheiten/statistiken?monat=${monatStr}`, { credentials: 'include' })
      .then(r => r.json()).then(d => { if (!d.error) setData(d); }).catch(() => {})
      .finally(() => setLoading(false));
  }, [monatStr]);

  // Load deviations when tab is active
  useEffect(() => {
    if (subTab !== 'deviations') return;
    setDevLoading(true);
    const url = devView === 'daily'
      ? `${API}/api/anwesenheiten/abweichungen/tag?datum=${devDate}`
      : `${API}/api/anwesenheiten/abweichungen/monat?monat=${devMonat}`;
    fetch(url, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!d.error) { devView === 'daily' ? setDayDevData(d) : setMonthDevData(d); } })
      .catch(() => {})
      .finally(() => setDevLoading(false));
  }, [subTab, devView, devDate, devMonat]);

  const SeverityBadge = ({ severity }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      severity === 'kritisch' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    }`}>
      {severity === 'kritisch' ? t('attendance.dev.critical') : t('attendance.dev.warnings')}
    </span>
  );

  return (
    <div>
      {/* Month selector + sub-tabs */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select value={monat} onChange={e => setMonat(Number(e.target.value))}
          className="bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]">
          {Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{tMonth(i)}</option>)}
        </select>
        <select value={jahr} onChange={e => setJahr(Number(e.target.value))}
          className="bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]">
          {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex items-center gap-1 ml-4 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
          {[
            { key: 'overview', label: t('attendance.stats.tab_overview', 'Overview') },
            { key: 'deviations', label: t('attendance.stats.tab_deviations', 'Deviations') },
          ].map(tb => (
            <button key={tb.key} onClick={() => setSubTab(tb.key)}
              className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
                subTab === tb.key
                  ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}>{tb.label}</button>
          ))}
        </div>
      </div>

      {/* Overview sub-tab */}
      {subTab === 'overview' && (
        <>
          {loading && <Spinner text={t('attendance.loading')} />}
          {!loading && data && (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <KpiCard label={t('attendance.stats.total_ma')} value={data.gesamt_ma} />
                <KpiCard label={t('attendance.stats.attendance_rate')} value={`${data.attendance_rate}%`} />
                <KpiCard label={t('attendance.stats.avg_hours')} value={`${data.avg_netto_tag_h}h`} sub={t('attendance.stats.per_day')} />
                <KpiCard label={t('attendance.stats.avg_break')} value={`${data.avg_pausen_tag_min}min`} sub={t('attendance.stats.per_day')} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <KpiCard label={t('attendance.stats.total_netto')} value={`${data.summe_netto_h}h`} />
                <KpiCard label={t('attendance.stats.total_soll')} value={`${data.summe_soll_h}h`} />
                <KpiCard label={t('attendance.stats.overtime_h')} value={`+${data.summe_ueberstunden_h}h`} sub={`${data.tage_ueberstunden} ${t('attendance.stats.days')}`} />
                <KpiCard label={t('attendance.stats.deficit_h')} value={`${data.summe_unterstunden_h}h`} sub={`${data.tage_unterstunden} ${t('attendance.stats.days')}`} />
              </div>

              {/* Pausen-Analyse + Pünktlichkeit */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {/* Pausen-Analyse */}
                <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-4">
                  <h3 className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('attendance.stats.break_analysis')}</h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('attendance.stats.avg_break_day')}</span>
                      <span className="font-mono font-bold text-[var(--text-primary)]">{data.pausen?.avg_pro_tag_min || 0}min</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('attendance.stats.longest_break')}</span>
                      <span className="font-mono font-bold text-[var(--text-primary)]">{data.pausen?.max_an_einem_tag_min || 0}min</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('attendance.stats.days_no_break')}</span>
                      <span className="font-mono font-bold text-[var(--text-primary)]">{data.pausen?.tage_ohne_pause || 0}</span>
                    </div>
                    {data.pausen?.ma_ohne_pause?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[var(--border-card,#E5E7EB)]">
                        <div className="text-[10px] text-[var(--text-tertiary)] uppercase mb-1">{t('attendance.stats.ma_no_break')}</div>
                        {data.pausen.ma_ohne_pause.map((r, i) => (
                          <div key={i} className="flex justify-between text-[11px]">
                            <span className="text-[var(--text-secondary)]">{r.name}</span>
                            <span className="font-mono text-red-400">{r.tage} {t('attendance.stats.days')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pünktlichkeit */}
                <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-4">
                  <h3 className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('attendance.stats.punctuality')}</h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('attendance.stats.avg_start')}</span>
                      <span className="font-mono font-bold text-[var(--text-primary)]">{data.puenktlichkeit?.avg_start_zeit || '–'}</span>
                    </div>
                    {data.puenktlichkeit?.fruehester_ma && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('attendance.stats.earliest_ma')}</span>
                        <span className="font-mono text-[var(--text-primary)]">{data.puenktlichkeit.fruehester_ma.name} ({data.puenktlichkeit.fruehester_ma.zeit})</span>
                      </div>
                    )}
                    {data.puenktlichkeit?.spaetester_ma && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('attendance.stats.latest_ma')}</span>
                        <span className="font-mono text-[var(--text-primary)]">{data.puenktlichkeit.spaetester_ma.name} ({data.puenktlichkeit.spaetester_ma.zeit})</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('attendance.stats.late_days')}</span>
                      <span className="font-mono font-bold text-[var(--text-primary)]">{data.puenktlichkeit?.tage_zu_spaet || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Overtime / Deficit */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-4">
                  <h3 className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('attendance.stats.top_overtime')}</h3>
                  {data.top_overtime.length === 0 ? (
                    <div className="text-xs text-[var(--text-tertiary)]">–</div>
                  ) : (
                    <div className="space-y-2">
                      {data.top_overtime.map((r, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-[var(--text-primary)]">{r.name}</span>
                          <span className="font-mono font-bold text-emerald-500">+{r.delta_h}h</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-4">
                  <h3 className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('attendance.stats.top_deficit')}</h3>
                  {data.top_deficit.length === 0 ? (
                    <div className="text-xs text-[var(--text-tertiary)]">–</div>
                  ) : (
                    <div className="space-y-2">
                      {data.top_deficit.map((r, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-[var(--text-primary)]">{r.name}</span>
                          <span className="font-mono font-bold text-red-400">{r.delta_h}h</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Deviations sub-tab */}
      {subTab === 'deviations' && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
              <button onClick={() => setDevView('daily')}
                className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${devView === 'daily' ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]' : 'text-[var(--text-secondary)]'}`}>
                {t('attendance.dev.daily')}
              </button>
              <button onClick={() => setDevView('monthly')}
                className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${devView === 'monthly' ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]' : 'text-[var(--text-secondary)]'}`}>
                {t('attendance.dev.monthly')}
              </button>
            </div>
            {devView === 'daily' ? (
              <input type="date" value={devDate} onChange={e => setDevDate(e.target.value)}
                className="bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)]" />
            ) : (
              <input type="month" value={devMonat} onChange={e => setDevMonat(e.target.value)}
                className="bg-[var(--bg-inset)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)]" />
            )}
          </div>

          {devLoading && <Spinner text={t('attendance.loading')} />}

          {/* Daily deviations */}
          {!devLoading && devView === 'daily' && dayDevData && (
            <>
              <div className="flex flex-wrap gap-3 mb-5">
                <KpiCard label={t('attendance.dev.present')} value={dayDevData.gesamt_mitarbeiter} />
                <KpiCard label={t('attendance.dev.with_deviations')} value={dayDevData.mit_abweichungen} />
                <KpiCard label={t('attendance.dev.critical')} value={dayDevData.kritisch} />
                <KpiCard label={t('attendance.dev.warnings')} value={dayDevData.warnungen} />
              </div>
              {dayDevData.abweichungen.length === 0 ? (
                <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-8 text-center text-sm text-[var(--text-tertiary)]">{t('attendance.dev.no_deviations')}</div>
              ) : (
                <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)] text-xs">
                        <th className="px-3 py-2 text-left w-8"></th>
                        <th className="px-3 py-2 text-left">{t('attendance.dev.col_name')}</th>
                        <th className="px-3 py-2 text-center">{t('attendance.dev.col_in')}</th>
                        <th className="px-3 py-2 text-center">{t('attendance.dev.col_out')}</th>
                        <th className="px-3 py-2 text-center">{t('attendance.dev.col_gross')}</th>
                        <th className="px-3 py-2 text-left">{t('attendance.dev.col_violations')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayDevData.abweichungen.map((row, idx) => {
                        const isExpanded = devExpanded === idx;
                        return (
                          <React.Fragment key={idx}>
                            <tr className="border-b border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset)] cursor-pointer"
                              onClick={() => setDevExpanded(isExpanded ? null : idx)}>
                              <td className="px-3 py-2.5"><ChevronIcon open={isExpanded} /></td>
                              <td className="px-3 py-2.5">
                                <button onClick={e => { e.stopPropagation(); if (row.mitarbeiter_id) onEmployeeClick(row.mitarbeiter_id); }}
                                  className="text-left font-medium text-[var(--text-primary)] hover:text-[#338edc]">{row.mitarbeiter_name}</button>
                              </td>
                              <td className="px-3 py-2.5 text-center font-mono text-xs">{row.check_in || '–'}</td>
                              <td className="px-3 py-2.5 text-center font-mono text-xs">{row.check_out || '–'}</td>
                              <td className="px-3 py-2.5 text-center font-mono text-xs">{fmtHours(row.brutto_h)}</td>
                              <td className="px-3 py-2.5">
                                <div className="flex flex-wrap gap-1">
                                  {row.regelverstoesse.map((v, vi) => <SeverityBadge key={vi} severity={v.schwere} />)}
                                </div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="border-b border-[var(--border-card,#E5E7EB)]">
                                <td colSpan={6} className="px-6 py-3 bg-[var(--bg-inset)]">
                                  <div className="space-y-2">
                                    {row.regelverstoesse.map((v, vi) => (
                                      <div key={vi} className="flex items-start gap-2">
                                        <SeverityBadge severity={v.schwere} />
                                        <span className="text-sm text-[var(--text-primary)]">{locale === 'de' ? v.beschreibung_de : v.beschreibung_en}</span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Monthly deviations */}
          {!devLoading && devView === 'monthly' && monthDevData && (
            <>
              <div className="flex flex-wrap gap-3 mb-5">
                <KpiCard label={t('attendance.dev.with_deviations')} value={monthDevData.mit_abweichungen} />
                <KpiCard label={t('attendance.dev.total_deviation_days')} value={monthDevData.gesamt_abweichungstage} />
              </div>
              {monthDevData.mitarbeiter.length === 0 ? (
                <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-8 text-center text-sm text-[var(--text-tertiary)]">{t('attendance.dev.no_deviations_month')}</div>
              ) : (
                <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)] text-xs">
                        <th className="px-3 py-2 text-left">{t('attendance.dev.col_name')}</th>
                        <th className="px-3 py-2 text-center">{t('attendance.dev.col_days')}</th>
                        <th className="px-3 py-2 text-center">{t('attendance.dev.col_no_break')}</th>
                        <th className="px-3 py-2 text-center">{t('attendance.dev.col_break_long')}</th>
                        <th className="px-3 py-2 text-center">{t('attendance.dev.col_over_12h')}</th>
                        <th className="px-3 py-2 text-center">{t('attendance.dev.col_open')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthDevData.mitarbeiter.map((row, idx) => {
                        const cellColor = (n) => { if (!n) return ''; if (n >= 5) return 'text-red-400 font-bold'; if (n >= 2) return 'text-amber-400 font-semibold'; return ''; };
                        return (
                          <tr key={idx} className="border-b border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset)]">
                            <td className="px-3 py-2.5">
                              <button onClick={() => row.mitarbeiter_id && onEmployeeClick(row.mitarbeiter_id)}
                                className="text-left font-medium text-[var(--text-primary)] hover:text-[#338edc]">{row.mitarbeiter_name}</button>
                            </td>
                            <td className={`px-3 py-2.5 text-center font-mono ${cellColor(row.abweichungstage)}`}>{row.abweichungstage}</td>
                            <td className={`px-3 py-2.5 text-center font-mono ${cellColor(row.details.keine_pause_30min)}`}>{row.details.keine_pause_30min || '–'}</td>
                            <td className={`px-3 py-2.5 text-center font-mono ${cellColor((row.details.pause_zu_lang_8h_plus || 0) + (row.details.pause_zu_lang_bis_8h || 0))}`}>{((row.details.pause_zu_lang_8h_plus || 0) + (row.details.pause_zu_lang_bis_8h || 0)) || '–'}</td>
                            <td className={`px-3 py-2.5 text-center font-mono ${cellColor(row.details.ueber_12h)}`}>{row.details.ueber_12h || '–'}</td>
                            <td className={`px-3 py-2.5 text-center font-mono ${cellColor(row.details.offene_buchung)}`}>{row.details.offene_buchung || '–'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  EinstellungenView — Stempel-Regeln + API-Key + Kiosk URL      */
/* ═══════════════════════════════════════════════════════════════ */

const REGEL_GROUPS = [
  {
    key: 'kurze_pausen',
    titleKey: 'attendance.settings.group_short_breaks',
    rules: ['kurze_pause_max_minuten', 'kurze_pause_max_anzahl', 'kurze_pause_fruehestens_nach', 'kurze_pause_abstand_minuten'],
  },
  {
    key: 'mittagspause',
    titleKey: 'attendance.settings.group_lunch',
    rules: ['mittag_pflicht_ab_stunden', 'mittag_min_minuten', 'mittag_max_minuten', 'mittag_fruehestens_nach'],
  },
  {
    key: 'arbeitszeit',
    titleKey: 'attendance.settings.group_worktime',
    rules: ['pause_fruehestens_nach_start', 'max_arbeitszeit_ohne_pause', 'fruehester_beginn', 'auto_ausstempel_nach', 'warnung_ueberzug_minuten'],
  },
];

const REGEL_LABELS = {
  kurze_pause_max_minuten: 'attendance.regel.kurze_pause_max',
  kurze_pause_fruehestens_nach: 'attendance.regel.kurze_pause_nach',
  kurze_pause_abstand_minuten: 'attendance.regel.kurze_pause_abstand',
  kurze_pause_max_anzahl: 'attendance.regel.kurze_pause_anzahl',
  mittag_min_minuten: 'attendance.regel.mittag_min',
  mittag_max_minuten: 'attendance.regel.mittag_max',
  mittag_fruehestens_nach: 'attendance.regel.mittag_nach',
  mittag_pflicht_ab_stunden: 'attendance.regel.mittag_pflicht',
  pause_fruehestens_nach_start: 'attendance.regel.pause_nach_start',
  max_arbeitszeit_ohne_pause: 'attendance.regel.max_ohne_pause',
  warnung_ueberzug_minuten: 'attendance.regel.warnung_ueberzug',
  auto_ausstempel_nach: 'attendance.regel.auto_ausstempel',
  fruehester_beginn: 'attendance.regel.fruehester_beginn',
};

const EINHEIT_LABELS = {
  minuten: 'min',
  min: 'min',
  stunden: 'h',
  anzahl: '×',
};

/**
 * Terminal-PIN (#1816). Die PIN steht seit v2.871.0 NICHT mehr im Klartext in
 * der Oberfläche — sie liegt nur als bcrypt-Hash vor und ist genau einmal
 * sichtbar: in der Antwort auf „Generieren". Wer sie verliert, generiert eine
 * neue; es gibt keinen Weg zurück zum Klartext (analog #1806).
 *
 * Ihre Rolle hat sich verschoben: sie ist kein Zugang mehr (das regelt die
 * Registrierung), sondern der Betriebsschalter — Entsperren eines deaktivierten
 * Terminals und Notweg für ein nicht registriertes Ersatzgerät.
 */
function KioskPinCard({ t }) {
  const [pinSet, setPinSet] = useState(false);
  const [neuePin, setNeuePin] = useState('');
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/einstellungen/kiosk-pin`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { setPinSet(!!d.pin_set); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const savePin = async () => {
    if (!/^\d{4,6}$/.test(editVal)) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/einstellungen/kiosk-pin`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: editVal }),
      });
      if (r.ok) { setPinSet(true); setNeuePin(''); setEditing(false); setEditVal(''); }
    } finally { setSaving(false); }
  };

  const generatePin = async () => {
    try {
      const r = await fetch(`${API}/api/einstellungen/kiosk-pin/generieren`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (r.ok && d.pin) { setNeuePin(d.pin); setPinSet(true); }
    } catch { /* */ }
  };

  if (!loaded) return null;

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
      <h3 className="text-sm font-bold text-[var(--text-primary)] mb-1">{t('stempel.einstellungen.kioskPin', 'Terminal-PIN')}</h3>
      <p className="text-[10px] text-[var(--text-tertiary)] mb-3">{t('stempel.einstellungen.adminPinDesc', 'Entsperrt ein deaktiviertes Terminal und dient als Notweg für nicht registrierte Geräte (4–6 Ziffern). Wird nur als Hash gespeichert und nach dem Setzen nicht mehr angezeigt.')}</p>

      <div className="flex items-center gap-3">
        {editing ? (
          <>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={editVal}
              onChange={e => setEditVal(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-28 bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-1.5 text-sm text-center font-mono tracking-[0.3em] text-[var(--text-primary)]"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') savePin(); if (e.key === 'Escape') setEditing(false); }}
            />
            <button onClick={savePin} disabled={saving || !/^\d{4,6}$/.test(editVal)}
              className="px-3 py-1.5 text-xs font-semibold bg-[#338edc] text-white rounded-lg disabled:opacity-40">
              {saving ? '...' : t('common.save', 'Speichern')}
            </button>
            <button onClick={() => setEditing(false)} className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
              {t('common.cancel', 'Abbrechen')}
            </button>
          </>
        ) : (
          <>
            <span className="text-xs font-medium px-3 py-1.5 rounded-lg"
              style={pinSet
                ? { background: '#DCFCE7', color: '#166534' }
                : { background: '#FEF3C7', color: '#92400E' }}>
              {pinSet
                ? t('stempel.einstellungen.pinGesetzt', 'PIN gesetzt')
                : t('stempel.einstellungen.pinFehlt', 'Keine PIN gesetzt')}
            </span>
            <button onClick={() => { setEditVal(''); setNeuePin(''); setEditing(true); }}
              className="px-3 py-1.5 text-xs font-medium text-[#338edc] border border-[#338edc]/30 rounded-lg hover:bg-[#338edc]/5">
              {t('common.edit', 'Ändern')}
            </button>
            <button onClick={generatePin}
              className="px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] border border-[var(--border-card,#E5E7EB)] rounded-lg hover:bg-[var(--bg-inset)]">
              {t('stempel.einstellungen.generieren', 'Generieren')}
            </button>
          </>
        )}
      </div>

      {neuePin && (
        <div className="mt-3 px-4 py-3 rounded-lg" style={{ background: '#FEF3C7', color: '#92400E' }}>
          <p className="text-[11px] font-semibold mb-1">
            {t('stempel.einstellungen.pinEinmalig', 'Jetzt notieren — die PIN wird nicht wieder angezeigt.')}
          </p>
          <code className="text-lg font-mono tracking-[0.3em]">{neuePin}</code>
        </div>
      )}
    </div>
  );
}


function EinstellungenView() {
  const { t } = useLanguage();
  const [regeln, setRegeln] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/stempel/regeln`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/api/einstellungen/stempel_api_key`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ value: '' })),
    ]).then(([regelData, keyData]) => {
      setRegeln(regelData.regeln || regelData.data || []);
      setApiKey(keyData.value || keyData.data || '');
    }).finally(() => setLoading(false));
  }, []);

  const handleRegelChange = (id, value) => {
    setRegeln(prev => prev.map(r => r.id === id ? { ...r, wert: parseFloat(value) || 0 } : r));
  };

  const handleSaveRegeln = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/stempel/regeln`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regeln: regeln.map(r => ({ id: r.id, wert: r.wert })) }),
      });
    } finally { setSaving(false); }
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Build a lookup by regel_key for grouped display
  const regelByKey = {};
  for (const r of regeln) regelByKey[r.regel_key] = r;

  return (
    <div className="space-y-6">
      {/* ModulEinstellungen for system_config keys */}
      <ModulEinstellungen kategorien={['stempeluhr']} excludeKeys={['kiosk_voice_enabled', 'kiosk_voice_id', 'kiosk_voice_mode', 'kiosk_voice_random']} />

      {/* Stempel-Regeln — Grouped */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-[var(--text-primary)]">{t('attendance.settings.rules_title')}</h3>
            <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{t('attendance.settings.rules_subtitle')}</p>
          </div>
          <button onClick={handleSaveRegeln} disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#338edc] text-white rounded-lg hover:bg-[#2a7bc8] disabled:opacity-40">
            <Save className="w-3 h-3" /> {saving ? '...' : t('common.save')}
          </button>
        </div>
        {loading ? <Spinner /> : (
          <div className="space-y-5">
            {REGEL_GROUPS.map(group => {
              const groupRules = group.rules.map(k => regelByKey[k]).filter(Boolean);
              if (!groupRules.length) return null;
              return (
                <div key={group.key}>
                  <div className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">{t(group.titleKey)}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {groupRules.map(r => (
                      <div key={r.id} className="flex items-center justify-between gap-3 bg-[var(--bg-inset)] rounded-lg px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-[var(--text-secondary)]">
                            {t(REGEL_LABELS[r.regel_key] || r.regel_key, r.beschreibung || r.regel_key)}
                          </div>
                          {r.beschreibung && (
                            <div className="text-[9px] text-[var(--text-muted)] mt-0.5 truncate">{r.beschreibung}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <input type="number" value={r.wert} onChange={e => handleRegelChange(r.id, e.target.value)}
                            className="w-20 bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded px-2 py-1 text-sm text-[var(--text-primary)] text-right font-mono" />
                          <span className="text-[10px] text-[var(--text-tertiary)] w-5">{EINHEIT_LABELS[r.einheit] || r.einheit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Kiosk-PIN */}
      <KioskPinCard t={t} />

      {/* API-Key (Legacy) */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[var(--text-primary)] mb-3">{t('attendance.settings.api_key_title')}</h3>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs font-mono text-[#338edc] bg-[var(--bg-inset)] px-3 py-2 rounded-lg truncate">
            {apiKey || '–'}
          </code>
          <button onClick={copyApiKey}
            className="p-2 rounded-lg border border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            title={t('common.copy')}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
        {copied && <span className="text-[10px] text-emerald-500 mt-1 block">{t('common.copied')}</span>}
        <p className="text-[10px] text-[var(--text-tertiary)] mt-2">{t('attendance.settings.api_key_hint')}</p>
      </div>

      {/* Kiosk URL */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[var(--text-primary)] mb-3">{t('attendance.kiosk_url_title')}</h3>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs font-mono text-[#338edc] bg-[var(--bg-inset)] px-3 py-2 rounded-lg truncate">
            {window.location.origin}/kiosk
          </code>
          <a href={`${window.location.origin}/kiosk`} target="_blank" rel="noopener noreferrer"
            className="p-2 rounded-lg border border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
        <p className="text-[10px] text-[var(--text-tertiary)] mt-2">{t('attendance.kiosk_url_hint')}</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  Main Component                                                 */
/* ═══════════════════════════════════════════════════════════════ */
export default function Anwesenheiten() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const maFilter = searchParams.get('ma');
  const [view, setView] = useState('tag');

  // #1426: Nur 'anwesenheiten_readonly' (ohne 'mitarbeiter') → kein Einstellungen-Tab, kein Sprung in MA-Stammdaten
  const mitarbeiterPerm = user?.permissions?.mitarbeiter;
  const hatHrZugriff = !!mitarbeiterPerm && mitarbeiterPerm !== 'none';

  const handleEmployeeClick = hatHrZugriff ? (localId) => navigate(`/mitarbeiter/${localId}`) : () => {};

  const tabs = [
    { key: 'tag', label: t('attendance.tab_day') },
    { key: 'monat', label: t('attendance.tab_month') },
    { key: 'stats', label: t('attendance.tab_stats') },
    ...(hatHrZugriff ? [{ key: 'einstellungen', label: t('modulSettings.title', 'Einstellungen') }] : []),
  ];

  return (
    <div className="p-page">
      <PageHeader title={t('attendance.title')} subtitle={t('attendance.subtitle')} className="mb-6" />

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] w-fit">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setView(tab.key)}
            className={`px-4 py-2 text-xs font-semibold rounded-[8px] transition-all ${
              view === tab.key
                ? 'bg-white shadow-sm text-[var(--text-primary)] dark:bg-[var(--bg-card)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {view === 'tag' && <DayView onEmployeeClick={handleEmployeeClick} maFilter={maFilter} />}
      {view === 'monat' && <MonthView onEmployeeClick={handleEmployeeClick} maFilter={maFilter} />}
      {view === 'stats' && <StatsView onEmployeeClick={handleEmployeeClick} />}
      {view === 'einstellungen' && <EinstellungenView />}
    </div>
  );
}
